import { useEffect, useMemo, useState } from "react";
import { Loader2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promptTitle: string;
  /** Ordered, de-duped variable names parsed from the prompt body. */
  variables: string[];
  /** Called with the filled-in `{name → value}` map once the user clicks Run. */
  onRun: (values: Record<string, string>) => void;
};

/**
 * Modal that asks the user to fill in every `{placeholder}` token detected
 * in a prompt body before the prompt is pushed to AI Chat. All fields are
 * required (no defaults in v1). Cancel closes without dispatching anything.
 */
export const PromptFillInDialog = ({
  open,
  onOpenChange,
  promptTitle,
  variables,
  onRun,
}: Props) => {
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    // Reset on open so a previous fill-in doesn't bleed across runs.
    const fresh: Record<string, string> = {};
    for (const name of variables) fresh[name] = "";
    setValues(fresh);
  }, [open, variables]);

  const allFilled = useMemo(
    () => variables.every((name) => (values[name] ?? "").trim().length > 0),
    [variables, values],
  );

  const handleRun = () => {
    if (!allFilled) return;
    onRun(values);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Fill in placeholders</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">"{promptTitle}"</span> contains{" "}
            {variables.length} {variables.length === 1 ? "placeholder" : "placeholders"}. Fill
            them in, then Run to send the finished prompt to AI Chat.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          {variables.map((name) => {
            const v = values[name] ?? "";
            const long = v.length > 80;
            return (
              <div key={name} className="grid gap-1.5">
                <Label htmlFor={`pf-${name}`} className="font-mono text-xs">
                  {`{${name}}`}
                </Label>
                {long ? (
                  <Textarea
                    id={`pf-${name}`}
                    value={v}
                    rows={3}
                    onChange={(e) =>
                      setValues((cur) => ({ ...cur, [name]: e.target.value }))
                    }
                    autoFocus={variables[0] === name}
                  />
                ) : (
                  <Input
                    id={`pf-${name}`}
                    value={v}
                    onChange={(e) =>
                      setValues((cur) => ({ ...cur, [name]: e.target.value }))
                    }
                    autoFocus={variables[0] === name}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && allFilled) {
                        e.preventDefault();
                        handleRun();
                      }
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleRun} disabled={!allFilled}>
            {allFilled ? <Play className="mr-1 h-3.5 w-3.5" /> : <Loader2 className="mr-1 h-3.5 w-3.5 opacity-50" />}
            Run
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromptFillInDialog;
